import { useEffect, useState } from "react";
import { enablePush, pushState, pushSupport } from "../lib/push";
import { BellIcon, CloseIcon } from "./Icons";

/*
 * Asking for push permission at the moment it's obviously useful (PLAN.md
 * §Notifications): after a first RSVP, after asking people ahead, or when an
 * owner or admin first sees someone asking to join. One soft ask; "Not now"
 * means the app never asks again (Settings still offers it). On iOS in a
 * browser tab, where a permission prompt can't work, a one-time hint to add
 * the app to the home screen instead.
 */

export type PushMoment = "rsvp" | "question" | "requests";
const COPY: Record<PushMoment, { title: string; line: string }> = {
  rsvp: { title: "Get a reminder the day before?", line: "Pagemates can tell you the day before, and if the date moves." },
  question: { title: "Want to know when someone answers?", line: "You'll hear who answered. The answer itself waits in Talk, sealed if it's past where you are." },
  requests: { title: "Hear when people ask to join?", line: "So nobody waits long for an answer." },
};

const KEY = "pm_push_ask"; // "dismissed" once someone says not now, or turns it on
const IOS_KEY = "pm_push_ios_hint";
const get = (k: string) => { try { return localStorage.getItem(k); } catch { return "dismissed"; } };
const set = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* fine */ } };

let listener: ((m: PushMoment) => void) | null = null;
/** Call right after the moment happens. Does nothing unless an ask is due. */
export function askForPush(moment: PushMoment) { listener?.(moment); }

export function PushAskHost() {
  const [ask, setAsk] = useState<{ moment: PushMoment; ios: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    listener = (moment) => {
      const support = pushSupport();
      if (support === "ios-install") { if (!get(IOS_KEY)) setAsk({ moment, ios: true }); return; }
      if (support !== "ok" || get(KEY) || Notification.permission !== "default") return;
      void pushState().then((s) => { if (s === "off") setAsk({ moment, ios: false }); });
    };
    return () => { listener = null; };
  }, []);
  if (!ask) return null;
  const close = () => { set(ask.ios ? IOS_KEY : KEY, "dismissed"); setAsk(null); };
  return (
    <div className="push-ask" role="dialog" aria-label="Notifications">
      <span className="pa-ico" aria-hidden="true"><BellIcon width={20} height={20} /></span>
      <div className="pa-text">
        {ask.ios ? (
          <><b>Add Pagemates to your Home Screen to get notifications</b><span>In Safari, tap Share, then Add to Home Screen, and open it from there.</span></>
        ) : (
          <><b>{COPY[ask.moment].title}</b><span>{COPY[ask.moment].line}</span></>
        )}
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          {ask.ios ? <button type="button" className="btn sm" onClick={close}>Got it</button> : <>
            <button type="button" className="btn primary sm" disabled={busy} onClick={() => { setBusy(true); void enablePush().finally(() => { setBusy(false); close(); }); }}>Turn on notifications</button>
            <button type="button" className="btn ghost sm" onClick={close}>Not now</button>
          </>}
        </div>
      </div>
      <button type="button" className="pa-x" aria-label="Close" onClick={close}><CloseIcon width={18} height={18} /></button>
    </div>
  );
}
